import React from 'react';
import style from './RichTextRenderer.css';

const RichTextRenderer = ({ content }) => {
  if (!content) return null;

  let rawContent;
  try {
    rawContent = JSON.parse(content);
  } catch (e) {
    console.error('Invalid JSON content', e);
    return null;
  }

  const applyInlineStyles = (text, styles, key) => {
    return styles.reduce((acc, styleType) => {
      switch (styleType) {
        case 'BOLD':
          return <strong key={`bold-${key}`}>{acc}</strong>;
        case 'ITALIC':
          return <em key={`italic-${key}`}>{acc}</em>;
        case 'UNDERLINE':
          return <u key={`underline-${key}`}>{acc}</u>;
        case 'STRIKETHROUGH':
          return <del key={`del-${key}`}>{acc}</del>;
        case 'SUPERSCRIPT':
          return <sup key={`sup-${key}`}>{acc}</sup>;
        case 'SUBSCRIPT':
          return <sub key={`sub-${key}`}>{acc}</sub>;
        case 'CODE':
        case 'MONOSPACE':
          return <code key={`code-${key}`}>{acc}</code>;
        default:
          return acc;
      }
    }, text);
  };

  const getStyleMap = (inlineStyleRanges) => {
    var map = {};
    inlineStyleRanges.forEach(function(range) {
      for (var i = range.offset; i < range.offset + range.length; i++) {
        if (!map[i]) map[i] = new Set();
        map[i].add(range.style);
      }
    });
    return map;
  };

  const renderTextWithEntitiesAndStyles = (block) => {
    var text = block.text;
    var entityRanges = block.entityRanges;
    var inlineStyleRanges = block.inlineStyleRanges;
    var entityMap = rawContent.entityMap;
    var styleMap = getStyleMap(inlineStyleRanges);

    var entityIndexMap = {};
    entityRanges.forEach(function(range) {
      for (var i = range.offset; i < range.offset + range.length; i++) {
        entityIndexMap[i] = {
          ...range,
          entity: entityMap[range.key],
        };
      }
    });

    var output = [];
    var current = '';
    var currentStyles = [];
    var currentEntity = null;

    function flush(text, styles, entity, index) {
      if (!text) return;
      var key = index + '-' + Math.random();
      var styledText = applyInlineStyles(text, styles, key);
      if (entity && entity.type === 'LINK') {
        output.push(
          <a
            key={'link-' + key}
            href={entity.data.url}
            target="_blank"
            rel="noopener noreferrer"
            className={style.link}
            style={{ color: 'blue', textDecoration: 'underline' }}
          >
            {styledText}
          </a>
        );
      } else {
        output.push(<React.Fragment key={key}>{styledText}</React.Fragment>);
      }
    }

    for (var i = 0; i < text.length; i++) {
      var char = text[i];
      var stylesAtChar = Array.from(styleMap[i] || []);
      var entityAtChar = entityIndexMap[i] ? entityIndexMap[i].entity : null;
      var shouldFlush =
        current &&
        (entityAtChar !== currentEntity || stylesAtChar.join(',') !== currentStyles.join(','));
      if (shouldFlush) {
        flush(current, currentStyles, currentEntity, i);
        current = '';
      }
      current += char;
      currentStyles = stylesAtChar;
      currentEntity = entityAtChar;
    }
    flush(current, currentStyles, currentEntity, text.length);
    return output;
  };

  const renderBlock = (block) => {
    var key = block.key;
    var type = block.type;
    var content = renderTextWithEntitiesAndStyles(block);
    switch (type) {
      case 'header-one':
        return <h1 key={key}>{content}</h1>;
      case 'header-two':
        return <h2 key={key}>{content}</h2>;
      case 'header-three':
        return <h3 key={key}>{content}</h3>;
      case 'header-four':
        return <h4 key={key}>{content}</h4>;
      case 'header-five':
        return <h5 key={key}>{content}</h5>;
      case 'header-six':
        return <h6 key={key}>{content}</h6>;
      case 'blockquote':
        return <blockquote key={key}>{content}</blockquote>;
      case 'code-block':
        return (
          <pre key={key}>
            <code>{block.text}</code>
          </pre>
        );
      case 'unordered-list-item':
      case 'ordered-list-item':
        return <li key={key}>{content}</li>;
      default:
        return <p key={key}>{content}</p>;
    }
  };

  const renderBlocksWithListHandling = (blocks) => {
    var output = [];
    var currentList = [];
    var currentType = null;
    blocks.forEach(function(block) {
      if (block.type === 'unordered-list-item' || block.type === 'ordered-list-item') {
        var type = block.type === 'unordered-list-item' ? 'ul' : 'ol';
        if (type !== currentType && currentList.length) {
          output.push(React.createElement(currentType, { key: output.length }, currentList));
          currentList = [];
        }
        currentType = type;
        currentList.push(renderBlock(block));
      } else {
        if (currentList.length) {
          output.push(React.createElement(currentType, { key: output.length }, currentList));
          currentList = [];
          currentType = null;
        }
        output.push(renderBlock(block));
      }
    });
    if (currentList.length) {
      output.push(React.createElement(currentType, { key: output.length }, currentList));
    }
    return output;
  };

  return <div>{renderBlocksWithListHandling(rawContent.blocks)}</div>;
};

export default RichTextRenderer;